import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface PageHeaderProps {
  /** Eyebrow left segment (workspace / account name). */
  eyebrow?: string;
  /** Eyebrow right segment after the divider, e.g. `OPERATE / DASHBOARD`. */
  eyebrowPath?: string;
  title: string;
  /** One sentence, numbers not adjectives. */
  subtitle?: string;
  /** Right side: zero or more tertiary buttons, then exactly one primary. */
  actions?: ReactNode;
  /** Alternative right side: StatCluster. */
  stats?: ReactNode;
  /** Smaller H1 (22px) for narrow windows or inspector headers. */
  compact?: boolean;
  className?: string;
}

/** Page header (DESIGN_SYSTEM section 7.4): eyebrow with divider, H1 28px medium tracking-tight, subtitle 14px --mute. */
export function PageHeader({ eyebrow, eyebrowPath, title, subtitle, actions, stats, compact = false, className }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-wrap items-end justify-between gap-4 border-b border-hair pb-6', className)}>
      <div className="min-w-0">
        {eyebrow || eyebrowPath ? (
          <p className="flex items-center gap-3 font-mono text-2xs uppercase tracking-[0.25em] text-mute">
            {eyebrow ? <span className="truncate">{eyebrow}</span> : null}
            {eyebrow && eyebrowPath ? <span aria-hidden className="h-3 w-px shrink-0 bg-line" /> : null}
            {eyebrowPath ? <span className="truncate">{eyebrowPath}</span> : null}
          </p>
        ) : null}
        <h1 className={cn('font-medium tracking-tight text-ink', eyebrow || eyebrowPath ? 'mt-3' : '', compact ? 'text-2xl' : 'text-[28px]')}>{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-mute">{subtitle}</p> : null}
      </div>
      {stats ? <div className="shrink-0">{stats}</div> : actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
